import { BehaviorSubject, Subject } from 'rxjs';
import { Inviter, Invitation, SessionState, UserAgent } from 'sip.js';
import { SessionDescriptionHandler } from 'sip.js/lib/platform/web';

export enum CallDirection {
  Inbound = 'Inbound',
  Outbound = 'Outbound',
}

export class EveCall {
  public direction: CallDirection;
  public remoteUser: string;
  public startTime: Date;
  public isOnHold: boolean = false;
  public duration: number = 0; // In seconds

  // New: session state tracking. Initialized with the session's current state.
  public session: Inviter | Invitation;
  private durationInterval: any = null;

  // --- new for DTMF feedback ---
  private audioContext: AudioContext;
  private dtmfFrequencies: Record<string, [number, number]> = {
    '1': [697, 1209],
    '2': [697, 1336],
    '3': [697, 1477],
    A: [697, 1633],
    '4': [770, 1209],
    '5': [770, 1336],
    '6': [770, 1477],
    B: [770, 1633],
    '7': [852, 1209],
    '8': [852, 1336],
    '9': [852, 1477],
    C: [852, 1633],
    '*': [941, 1209],
    '0': [941, 1336],
    '#': [941, 1477],
    D: [941, 1633],
  };

  constructor(session: Inviter | Invitation) {
    this.session = session;
    this.direction =
      session instanceof Invitation
        ? CallDirection.Inbound
        : CallDirection.Outbound;
    this.remoteUser = session.remoteIdentity.uri.toString();
    this.startTime = new Date();

    // Start tracking call duration.
    this.durationInterval = setInterval(() => {
      this.duration = Math.floor(
        (new Date().getTime() - this.startTime.getTime()) / 1000
      );
    }, 1000);

    // init WebAudio
    this.audioContext = new AudioContext();
  }

  async acceptCall(): Promise<void> {
    if (!(this.session instanceof Invitation)) return;
    try {
      await this.session.accept();
      console.log(`📞 Call with ${this.remoteUser} accepted.`);
      // Optionally, update the session state manually if needed.
      // This may already be updated by SIP.js.
    } catch (error) {
      console.error(`❌ Failed to accept call:`, error);
    }
  }

  async rejectCall(): Promise<void> {
    if (!(this.session instanceof Invitation)) return;
    try {
      await this.session.reject();
      console.log(`❌ Call with ${this.remoteUser} rejected.`);
    } catch (error) {
      console.error(`❌ Failed to reject call:`, error);
    }
  }

  async holdCall(): Promise<void> {
    if (!this.session) {
      console.warn('⚠️ No active call to hold.');
      return;
    }
    try {
      const options = {
        sessionDescriptionHandlerModifiers: [this.holdModifier.bind(this)],
        requestDelegate: {
          onAccept: () => {
            this.isOnHold = true;
            console.log(`✅ Call with ${this.remoteUser} held.`);
          },
          onReject: () => {
            console.warn(`❌ Call hold was rejected by ${this.remoteUser}.`);
            this.isOnHold = false;
          },
        },
      };
      this.isOnHold = true;
      await this.session.invite(options);
      console.log(`⏸️ Call with ${this.remoteUser} put on hold.`);
    } catch (error) {
      console.error(`❌ Failed to hold call:`, error);
    }
  }

  async unholdCall(): Promise<void> {
    if (!this.session) {
      console.warn('⚠️ No active call to resume.');
      return;
    }
    const options = {
      sessionDescriptionHandlerModifiers: [],
      requestDelegate: {
        onAccept: () => {
          this.isOnHold = false;
          console.log(`✅ Call with ${this.remoteUser} resumed.`);
        },
        onReject: () => {
          console.warn(`❌ Call resume was rejected by ${this.remoteUser}.`);
          this.isOnHold = true;
        },
      },
    };
    try {
      await this.session.invite(options);
      this.isOnHold = false;
      console.log(`▶️ Call with ${this.remoteUser} resumed.`);
    } catch (error) {
      console.error(`❌ Failed to unhold call:`, error);
    }
  }

  /**
   * Hang up this call.
   * - If we’re still in “Establishing”, send a CANCEL
   * - Otherwise send a BYE
   */
  async endCall(): Promise<void> {
    if (!this.session) return;

    try {
      // Outgoing call not yet answered → cancel
      if (
        this.session instanceof Inviter &&
        this.session.state === SessionState.Establishing
      ) {
        await this.session.cancel();
        console.log(`❌ Outgoing call to ${this.remoteUser} canceled.`);
      }
      // Already answered (or in early media) → normal hangup
      else {
        await this.session.bye();
        console.log(`🔚 Call with ${this.remoteUser} ended.`);
      }

      clearInterval(this.durationInterval);
    } catch (error) {
      console.error('❌ Failed to end call:', error);
    }
  }

  async sendDTMF(digit: string): Promise<void> {
    console.log(`🔢 Attempting to send RTP DTMF: '${digit}'...`);
    if (!this.session) {
      console.warn('⚠️ No active call session found. Cannot send DTMF.');
      return;
    }

    // play local tone
    this.playLocalDTMF(digit);

    const sessionDescriptionHandler = this.session
      .sessionDescriptionHandler as SessionDescriptionHandler;
    if (!sessionDescriptionHandler) {
      console.warn('⚠️ No SessionDescriptionHandler available for DTMF.');
      return;
    }
    const peerConnection = sessionDescriptionHandler.peerConnection;
    if (!peerConnection) {
      console.warn('⚠️ No PeerConnection found.');
      return;
    }
    let dtmfSender = peerConnection
      .getSenders()
      .find((sender) => sender.track?.kind === 'audio')?.dtmf;
    if (!dtmfSender) {
      console.log('❌ No DTMF sender found.');
      return;
    }
    try {
      dtmfSender.insertDTMF(digit, 800, 400);
      console.log(`✅ RTP DTMF '${digit}' sent successfully.`);
    } catch (err: any) {
      console.error(`❌ Failed to send DTMF '${digit}':`, err);
    }
  }

  transferTo(target: string, realm: string): Promise<void> {
    if (!this.session) {
      return Promise.reject(new Error('No session to transfer'));
    }

    const sipTarget = target.includes('@')
      ? `sip:${target}`
      : `sip:${target}@${realm}`;

    const targetURI = UserAgent.makeURI(sipTarget);
    if (!targetURI) {
      return Promise.reject(new Error('Invalid target URI'));
    }

    return this.session
      .refer(targetURI)
      .then(() => {
        console.log(
          `🔁 Call with ${this.remoteUser} transferred to ${sipTarget}`
        );
      })
      .catch((err) => {
        console.error(`❌ Transfer failed for ${this.remoteUser}`, err);
        throw err;
      });
  }

  private async holdModifier(description: any): Promise<any> {
    let sdp = description.sdp;
    sdp = sdp.replace(/a=sendrecv\r\n/g, 'a=sendonly\r\n');
    description.sdp = sdp;
    return description;
  }

  private playLocalDTMF(digit: string, duration = 200) {
    const freqs = this.dtmfFrequencies[digit];
    if (!freqs) return;
    const now = this.audioContext.currentTime;
    const gain = this.audioContext.createGain();
    gain.gain.setValueAtTime(0.3, now);
    gain.connect(this.audioContext.destination);

    const [f1, f2] = freqs;
    const o1 = this.audioContext.createOscillator();
    const o2 = this.audioContext.createOscillator();
    o1.frequency.value = f1;
    o2.frequency.value = f2;
    o1.connect(gain);
    o2.connect(gain);
    o1.start(now);
    o2.start(now);
    o1.stop(now + duration / 1000);
    o2.stop(now + duration / 1000);
  }
}
